import { useMemo } from 'react'
import { Users, Stethoscope, UserPlus, X } from 'lucide-react'
import SearchableSelect from '../SearchableSelect.jsx'
import { specOptions } from '../../lib/broadcast.js'
import { doctorCode, doctorFullName } from '../../lib/dentist.js'

/**
 * Who does this go to — ทุกท่าน / by SPEC / named doctors.
 *
 * The three modes are NOT exclusive in storage, and the UI says so: ทุกท่าน
 * overrides everything, and named doctors are always UNIONed on top of a spec
 * match. That mirrors the Xano `broadcast_audience` function exactly. If it
 * looked exclusive here, a manager who picked "Ortho + หมอเอ" would reasonably
 * expect one of the two to be ignored.
 *
 * ⚠️ Spec chips are derived FROM THE DATA (lib/broadcast.js#specOptions), never
 * from a hard-coded list: `Doctor.spec[]` is free text and live rows hold
 * `d-aligner` and `สปสช` alongside the appointmentList types. A fixed list would
 * silently drop real doctors from a send.
 *
 * There is deliberately no "ไม่ระบุ" chip. The directory has one because you can
 * FILTER on the absence of a spec; you cannot TARGET it — "everyone who has not
 * told us what they do" is not an audience. Those doctors are reachable through
 * ทุกท่าน or by name, and the reach panel counts how many a spec send skipped.
 */
export default function AudiencePicker({ value, onChange, doctors = [], disabled = false }) {
  const targetAll = Boolean(value?.target_all)
  const specs = value?.target_spec || []
  const picked = value?.target_doctor || []

  const specOpts = useMemo(() => specOptions(doctors), [doctors])

  const doctorOpts = useMemo(() => (
    (doctors || [])
      .filter((d) => d?.active !== false)
      .map((d) => ({
        value: String(d.id),
        label: doctorCode(d) || doctorFullName(d) || `หมอ #${d.id}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'th'))
  ), [doctors])

  const byId = useMemo(() => new Map((doctors || []).map((d) => [Number(d.id), d])), [doctors])

  const set = (patch) => onChange?.({ ...value, ...patch })

  return (
    <div className="space-y-3">
      <label className="block text-[13px] font-medium text-slate-600">ส่งถึง</label>

      {/* ทุกท่าน is a switch, not a third radio: it does not replace the other
          two, it overrides them, and greying them out is how that reads. */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => set({ target_all: !targetAll })}
        className={`w-full flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-[13.5px] text-left transition ${
          targetAll
            ? 'border-brand-300 bg-brand-50 text-brand-800'
            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'
        } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <Users size={15} strokeWidth={1.75} className="shrink-0" />
        <span className="font-medium">ทันตแพทย์ทุกท่าน</span>
        <span className="ml-auto text-[12px] text-slate-500">
          {targetAll ? 'เลือกอยู่' : 'แตะเพื่อเลือก'}
        </span>
      </button>

      <div className={targetAll ? 'opacity-40 pointer-events-none' : ''}>
        <div className="space-y-3">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5 text-[12.5px] text-slate-500">
              <Stethoscope size={13} strokeWidth={1.75} />
              <span>เลือกตามความเชี่ยวชาญ</span>
            </div>
            <SearchableSelect
              options={specOpts}
              values={new Set(specs)}
              multiple
              disabled={disabled || targetAll}
              placeholder="เลือก SPEC…"
              searchPlaceholder="พิมพ์ชื่อ SPEC…"
              emptyText="ไม่พบ SPEC"
              onChange={(next) => set({ target_spec: [...(next instanceof Set ? next : new Set(next || []))] })}
            />
            {specs.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {specs.map((s) => (
                  <Chip key={s} label={s} onRemove={() => set({ target_spec: specs.filter((x) => x !== s) })} />
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-1.5 text-[12.5px] text-slate-500">
              <UserPlus size={13} strokeWidth={1.75} />
              <span>เพิ่มรายบุคคล</span>
              {specs.length > 0 && <span className="text-slate-400">— เพิ่มจาก SPEC ข้างบน</span>}
            </div>
            <SearchableSelect
              options={doctorOpts}
              values={new Set(picked.map(String))}
              multiple
              disabled={disabled || targetAll}
              placeholder="เลือกคุณหมอ…"
              searchPlaceholder="พิมพ์ชื่อหรือรหัส…"
              emptyText="ไม่พบทันตแพทย์"
              onChange={(next) => {
                const s = next instanceof Set ? next : new Set(next || [])
                set({ target_doctor: [...s].map(Number).filter(Number.isFinite) })
              }}
            />
            {picked.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {picked.map((id) => {
                  const d = byId.get(Number(id))
                  return (
                    <Chip
                      key={id}
                      label={d ? (doctorCode(d) || doctorFullName(d)) : `หมอ #${id}`}
                      onRemove={() => set({ target_doctor: picked.filter((x) => Number(x) !== Number(id)) })}
                    />
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Chip({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-lg bg-slate-100 ring-1 ring-slate-200 px-2 py-0.5 text-[12px] text-slate-700">
      {label}
      <button type="button" onClick={onRemove} className="text-slate-400 hover:text-rose-600 transition-colors">
        <X size={12} strokeWidth={2} />
      </button>
    </span>
  )
}
